import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PromoteKit from "@/components/PromoteKit";
import AffiliateMarketingKit from "@/components/AffiliateMarketingKit";
import {
  DollarSign,
  Users,
  TrendingUp,
  Wallet,
  Banknote,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Copy,
  Clock,
  Loader2,
  ArrowRight,
  Trophy,
  Crown,
  Award,
  Link2,
  Plus,
  Trash2,
  QrCode,
  Download,
} from "lucide-react";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("af_token")}`,
});

export default function Affiliate() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [tier, setTier] = useState(null);
  const [leaderboard, setLeaderboard] = useState({ rows: [], my_rank: null });
  const [links, setLinks] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    loadAll();
    // After Stripe Connect onboarding, the URL has `?connect=return` — refresh status.
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect")) {
      toast.success("Connect onboarding updated — syncing your payout status.");
    }
  }, []);

  const loadAll = async () => {
    try {
      const me = await axios.get(`${API}/affiliate/me`, { headers: authHeader() });
      setStatus(me.data);
      if (me.data?.enrolled) {
        const [c, conv, p, t, lb, lk, dest] = await Promise.all([
          axios.get(`${API}/affiliate/commissions`, { headers: authHeader() }),
          axios.get(`${API}/affiliate/conversions`, { headers: authHeader() }),
          axios.get(`${API}/affiliate/payouts`, { headers: authHeader() }),
          axios.get(`${API}/affiliate/tier`, { headers: authHeader() }),
          axios.get(`${API}/affiliate/leaderboard`, { headers: authHeader() }),
          axios.get(`${API}/affiliate/links`, { headers: authHeader() }),
          axios.get(`${API}/affiliate/links/destinations`, { headers: authHeader() }),
        ]);
        setCommissions(c.data?.commissions || []);
        setConversions(conv.data?.conversions || []);
        setPayouts(p.data?.payouts || []);
        setTier(t.data || null);
        setLeaderboard({ rows: lb.data?.leaderboard || [], my_rank: lb.data?.my_rank });
        setLinks(lk.data?.links || []);
        setDestinations(dest.data?.destinations || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load affiliate data");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/affiliate/apply`, {}, { headers: authHeader() });
      toast.success("Welcome to the iSpeedToClose Affiliate Program!");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't enroll");
    } finally {
      setApplying(false);
    }
  };

  const switchPayoutMethod = async (method) => {
    try {
      await axios.post(
        `${API}/affiliate/payout-method`,
        { method },
        { headers: authHeader() }
      );
      toast.success(
        method === "stripe_connect"
          ? "Payouts will go to your bank via Stripe Connect."
          : "Payouts will be applied as account credits."
      );
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update payout method");
    }
  };

  const startConnectOnboarding = async () => {
    setConnectLoading(true);
    try {
      const r = await axios.post(
        `${API}/affiliate/connect/onboarding-link`,
        {},
        { headers: authHeader() }
      );
      if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Connect onboarding");
      setConnectLoading(false);
    }
  };

  const copyLink = () => {
    const code = user?.referral_code;
    if (!code) {
      toast.error("Referral code not found");
      return;
    }
    const url = `${window.location.origin}/refer/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied!");
  };

  if (loading) {
    return (
      <div className="space-y-8" data-testid="affiliate-loading">
        <div className="h-8 w-48 bg-slate-100 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!status?.enrolled) {
    return <NotEnrolled status={status} onApply={apply} applying={applying} />;
  }

  const acct = status.account || {};
  const stats = status.stats || {};
  const policy = status.policy || {};

  return (
    <div className="space-y-8" data-testid="affiliate-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Sparkles size={11} /> Affiliate Program
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            {policy.commission_rate_pct}% recurring for {policy.commission_window_months} months
          </h1>
          <p className="text-[var(--ink-2)] mt-1">
            Every paying customer you refer earns you {policy.commission_rate_pct}% of their MRR for {policy.commission_window_months} months.
            Payouts on the {policy.payout_cadence} once you cross ${policy.min_payout_usd}.
          </p>
        </div>
        <button
          onClick={copyLink}
          className="btn-money"
          data-testid="copy-affiliate-link"
        >
          <Copy size={14} /> Copy share link
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Wallet}
          label="Pending balance"
          value={`$${(stats.pending_balance_cents / 100).toFixed(2)}`}
          accent="#10B981"
          testid="stat-pending"
        />
        <StatCard
          icon={Banknote}
          label="Lifetime earned"
          value={`$${(stats.lifetime_commission_cents / 100).toFixed(2)}`}
          accent="#0F172A"
          testid="stat-lifetime"
        />
        <StatCard
          icon={Users}
          label="Active referrals"
          value={`${stats.active_conversions} / ${stats.lifetime_conversions}`}
          accent="#3B82F6"
          testid="stat-referrals"
        />
        <StatCard
          icon={TrendingUp}
          label="Lifetime paid out"
          value={`$${(stats.lifetime_payout_cents / 100).toFixed(2)}`}
          accent="#7C3AED"
          testid="stat-paid"
        />
      </div>

      {/* Tier Badge + Leaderboard (Phase 2A) */}
      {tier && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="affiliate-tier-row">
          <TierBadge tier={tier} />
          <div className="lg:col-span-2">
            <Leaderboard
              rows={leaderboard.rows}
              myRank={leaderboard.my_rank}
              myTier={tier?.tier}
            />
          </div>
        </div>
      )}

      {/* Deep-Link Generator (Phase 2B) */}
      <LinksManager
        links={links}
        destinations={destinations}
        onChange={loadAll}
        affiliateCode={user?.referral_code}
      />

      {/* Marketing Kit — operator-flavored copy per destination (ebooks + library + marketplace) */}
      <AffiliateMarketingKit />

      {/* Promote Kit — swipe copy, QR, social cards, signature, perf */}
      <PromoteKit />

      {/* Payout method */}
      <PayoutMethodPicker
        account={acct}
        onSwitch={switchPayoutMethod}
        onStartConnect={startConnectOnboarding}
        connectLoading={connectLoading}
      />

      {/* Conversions table */}
      {/* Promo Assets — approved marketing images for affiliates to share */}
      <section data-testid="affiliate-assets-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl">Promo Assets</h2>
          <span className="text-xs text-[var(--ink-3)]">
            Approved by iSpeedToClose · updated as new pieces drop
          </span>
        </div>
        <p className="text-sm text-[var(--ink-2)] mb-4">
          Use these images to promote iSpeedToClose on social, email, print, or your own website.
          Please include your referral link on every share so you get credit for the conversion.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              file: "01_flyer_close-more-deals.png",
              title: "Full-Page Flyer",
              use:   "Print handouts · Direct mail · Booth signage",
              format:"Portrait · Print-ready",
            },
            {
              file: "02_spotlight_wholesaling-platform.png",
              title: "Platform Spotlight",
              use:   "Facebook · LinkedIn · Newsletter feature",
              format:"Portrait · Digital",
            },
            {
              file: "03_social_close-more-deals-faster.png",
              title: "Social — Close More Deals Faster",
              use:   "Instagram · Twitter/X · Text-message share",
              format:"Square · Digital",
            },
            {
              file: "04_flyer_luxury-home.png",
              title: "Luxury Home Flyer",
              use:   "Print handouts · Investor-club events · Real-estate meetups",
              format:"Portrait · Print-ready",
            },
            {
              file: "05_dashboard_every-deal.png",
              title: "Dashboard Hero",
              use:   "LinkedIn · Twitter/X · Product-tour posts",
              format:"Square · Digital",
            },
            {
              file: "06_tablet-phone_close-more-deals.png",
              title: "Tablet + Phone — Close More Deals",
              use:   "Facebook · Instagram · Newsletter feature",
              format:"Square · Digital",
            },
            {
              file: "07_phone_close-more-deals-faster.png",
              title: "Phone — All Your Wholesaling Tools",
              use:   "Instagram · Twitter/X · Story graphics",
              format:"Square · Digital",
            },
            {
              file: "08_comparison_stop-paying.png",
              title: "Stack Comparison — Stop Paying for 5 Tools",
              use:   "Sales emails · Landing pages · Compare-vs-competitor posts",
              format:"Portrait · Digital",
            },
            {
              file: "09_flyer_no-more-paying.png",
              title: "No More Paying — All-in-One Platform Flyer",
              use:   "Print handouts · Direct mail · Instagram · Facebook",
              format:"Portrait · Print-ready",
            },
          ].map((a) => (
            <div key={a.file} className="card p-3 flex flex-col"
                 data-testid={`affiliate-asset-${a.file}`}>
              <a href={`/downloads/affiliate/${a.file}`}
                 target="_blank" rel="noopener noreferrer"
                 className="block overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:border-amber-400 transition-colors">
                <img src={`/downloads/affiliate/${a.file}`}
                     alt={a.title}
                     className="w-full aspect-[4/5] object-cover" loading="lazy"/>
              </a>
              <div className="mt-3 flex-1">
                <div className="font-semibold text-[var(--ink)]">{a.title}</div>
                <div className="text-xs text-[var(--ink-3)] mt-0.5">{a.format}</div>
                <div className="text-xs text-[var(--ink-2)] mt-1.5">{a.use}</div>
              </div>
              <a href={`/downloads/affiliate/${a.file}`}
                 download
                 data-testid={`affiliate-asset-download-${a.file}`}
                 className="btn-outline mt-3 justify-center text-xs">
                <Download size={12}/> Download
              </a>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-[var(--ink-3)] leading-relaxed">
          <b>Compliance reminder:</b> Do not modify these assets or overlay text that
          changes the offer (e.g., don&apos;t add &quot;50% off&quot; or invent testimonials). Do include
          your referral link. Do include a short note in your own voice — audiences convert
          on people, not on ads.
        </div>
      </section>

      <section data-testid="conversions-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl">Referrals</h2>
          <span className="text-xs text-[var(--ink-3)]">{conversions.length} total</span>
        </div>
        {conversions.length === 0 ? (
          <EmptyState
            text="No paying referrals yet. Share your link to start earning."
            cta={
              <button onClick={copyLink} className="btn-outline">
                <Copy size={14} /> Copy share link
              </button>
            }
          />
        ) : (
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[var(--ink-3)]">
                <tr>
                  <th className="text-left px-4 py-3">Referred user</th>
                  <th className="text-left px-4 py-3">Tier @ conversion</th>
                  <th className="text-left px-4 py-3">Current tier</th>
                  <th className="text-left px-4 py-3">First paid</th>
                  <th className="text-left px-4 py-3">Window ends</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {conversions.map((c) => (
                  <tr key={c.id} data-testid={`conversion-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{c.referred_name || "—"}</div>
                      <div className="text-xs text-[var(--ink-3)]">{c.referred_email_masked}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{c.tier_at_conversion || "—"}</td>
                    <td className="px-4 py-3 capitalize">{c.referred_current_tier}</td>
                    <td className="px-4 py-3 text-xs text-[var(--ink-2)]">
                      {fmtDate(c.first_paid_at)}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--ink-2)]">
                      {fmtDate(c.commission_window_ends_at)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Commissions table */}
      <section data-testid="commissions-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl">Recent commissions</h2>
          <span className="text-xs text-[var(--ink-3)]">{commissions.length} total</span>
        </div>
        {commissions.length === 0 ? (
          <EmptyState text="Commissions accrue when a referred customer is billed by Stripe." />
        ) : (
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[var(--ink-3)]">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Referred user</th>
                  <th className="text-right px-4 py-3">Invoice</th>
                  <th className="text-right px-4 py-3">Commission</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {commissions.slice(0, 25).map((c) => (
                  <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                    <td className="px-4 py-3 text-xs text-[var(--ink-2)]">{fmtDate(c.accrued_at)}</td>
                    <td className="px-4 py-3 text-xs">{c.referred_email_masked}</td>
                    <td className="px-4 py-3 text-right text-xs">${(c.invoice_amount_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold">
                      ${(c.commission_cents / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Payouts */}
      {payouts.length > 0 && (
        <section data-testid="payouts-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-xl">Payout history</h2>
            <span className="text-xs text-[var(--ink-3)]">{payouts.length} total</span>
          </div>
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[var(--ink-3)]">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Method</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {payouts.map((p) => (
                  <tr key={p.id} data-testid={`payout-row-${p.id}`}>
                    <td className="px-4 py-3 text-xs text-[var(--ink-2)]">{fmtDate(p.paid_at || p.created_at)}</td>
                    <td className="px-4 py-3 capitalize text-xs">{p.payout_method?.replace("_", " ")}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">${(p.commission_cents_total / 100).toFixed(2)}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// ─── sub-components ────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
        style={{ background: accent + "1a", color: accent }}
      >
        <Icon size={18} />
      </div>
      <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] mb-1">{label}</div>
      <div className="font-display font-extrabold text-2xl tracking-tight">{value}</div>
    </div>
  );
}

function PayoutMethodPicker({ account, onSwitch, onStartConnect, connectLoading }) {
  const current = account.payout_method || "credits";
  const connectReady = account.payouts_enabled && account.charges_enabled;
  const connectStarted = !!account.stripe_connect_account_id;
  const due = (account.requirements_due || []).length;

  return (
    <section className="card p-5" data-testid="payout-method-section">
      <h2 className="font-display font-bold text-lg mb-1">Payout method</h2>
      <p className="text-sm text-[var(--ink-2)] mb-4">
        Choose how you want to receive your commission. You can switch any time.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {/* Credits */}
        <button
          onClick={() => onSwitch("credits")}
          className="text-left p-4 rounded-xl border-2 transition-colors"
          style={{
            borderColor: current === "credits" ? "#10B981" : "var(--line)",
            background: current === "credits" ? "#ECFDF5" : "white",
          }}
          data-testid="payout-credits-card"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold flex items-center gap-2">
              <DollarSign size={16} /> Account credit
            </div>
            {current === "credits" && (
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                Active
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--ink-2)]">
            Apply your commission as a credit on your own next iSpeedToClose invoice. Zero KYC, instant.
          </p>
        </button>

        {/* Stripe Connect */}
        <button
          onClick={() => onSwitch("stripe_connect")}
          className="text-left p-4 rounded-xl border-2 transition-colors"
          style={{
            borderColor: current === "stripe_connect" ? "#10B981" : "var(--line)",
            background: current === "stripe_connect" ? "#ECFDF5" : "white",
          }}
          data-testid="payout-connect-card"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold flex items-center gap-2">
              <Banknote size={16} /> Bank transfer (Stripe Connect)
            </div>
            {current === "stripe_connect" && (
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                Active
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--ink-2)]">
            ACH deposit to your bank account. Stripe handles KYC + W9 / 1099 tax reporting.
          </p>
        </button>
      </div>

      {/* Connect onboarding state */}
      {current === "stripe_connect" && (
        <div className="mt-4 p-4 rounded-xl border border-[var(--line)] bg-slate-50" data-testid="connect-status-card">
          {connectReady ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5" />
              <div>
                <div className="font-semibold">Connect onboarding complete</div>
                <div className="text-sm text-[var(--ink-2)] mt-1">
                  Your bank account is verified. Payouts will deposit on the 1st of each month.
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <AlertCircle size={18} className="text-amber-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">
                  {connectStarted ? "Onboarding incomplete" : "Set up bank payouts"}
                </div>
                <div className="text-sm text-[var(--ink-2)] mt-1 mb-3">
                  {connectStarted && due > 0 ? (
                    <>Stripe needs {due} more piece{due === 1 ? "" : "s"} of info before you can receive transfers.</>
                  ) : (
                    <>Connect your bank account through Stripe. Takes about 3 minutes — Stripe handles KYC + W9 for you.</>
                  )}
                </div>
                <button
                  onClick={onStartConnect}
                  disabled={connectLoading}
                  className="btn-money"
                  data-testid="start-connect-onboarding-btn"
                >
                  {connectLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ExternalLink size={14} />
                  )}
                  {connectStarted ? "Continue onboarding" : "Set up payouts on Stripe"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function NotEnrolled({ status, onApply, applying }) {
  const policy = status?.policy || {};
  const eligible = status?.eligible;
  return (
    <div className="max-w-3xl" data-testid="affiliate-onboarding">
      <div className="label-eyebrow flex items-center gap-1.5">
        <Sparkles size={11} /> Affiliate Program
      </div>
      <h1 className="font-display font-extrabold text-4xl tracking-tight mt-1 mb-2">
        Get paid for every customer you bring us.
      </h1>
      <p className="text-[var(--ink-2)] text-lg mb-8 leading-relaxed">
        Earn <strong>{policy.commission_rate_pct || 30}% recurring for {policy.commission_window_months || 12} months</strong> on every paying customer you refer.
        That&apos;s ~$356 lifetime per Pro signup — passive income while you keep wholesaling.
      </p>

      <div className="grid md:grid-cols-3 gap-3 mb-8">
        <PolicyTile label="Commission" value={`${policy.commission_rate_pct || 30}%`} sub={`for ${policy.commission_window_months || 12} months`} />
        <PolicyTile label="Cookie window" value={`${policy.cookie_window_days || 60} days`} sub="first-touch attribution" />
        <PolicyTile label="Payout" value={`$${policy.min_payout_usd || 50}+`} sub={policy.payout_cadence || "monthly"} />
      </div>

      <div className="card p-6" data-testid="affiliate-apply-card">
        <h2 className="font-display font-bold text-xl mb-3">How it works</h2>
        <ol className="space-y-3 text-[var(--ink-2)] mb-6">
          <Step n={1}>You apply (right now — takes 2 seconds).</Step>
          <Step n={2}>Share your link anywhere — YouTube, X, REI groups, newsletter.</Step>
          <Step n={3}>When someone signs up + pays, we attribute it to you for the next {policy.commission_window_months || 12} months.</Step>
          <Step n={4}>Get paid on the 1st of every month — choose bank transfer (Stripe Connect) or account credit.</Step>
        </ol>

        {eligible ? (
          <button
            onClick={onApply}
            disabled={applying}
            className="btn-money"
            data-testid="affiliate-apply-btn"
          >
            {applying ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {applying ? "Enrolling…" : "Join the program"}
          </button>
        ) : (
          <div className="banner-warn flex items-start gap-3">
            <AlertCircle size={16} className="mt-0.5" />
            <div className="text-sm">
              <strong>You need an active paid plan</strong> to join the affiliate program.{" "}
              <Link to="/app/billing" className="underline font-semibold">
                Upgrade here
              </Link>{" "}
              and come right back.
            </div>
          </div>
        )}
      </div>

      {/* Media-kit preview — shows the assets prospects will receive */}
      <div className="mt-8" data-testid="affiliate-assets-preview">
        <h2 className="font-display font-bold text-xl mb-1">Media kit preview</h2>
        <p className="text-sm text-[var(--ink-2)] mb-4">
          A peek at the promo assets you&apos;ll have access to once enrolled.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            "05_dashboard_every-deal.png",
            "07_phone_close-more-deals-faster.png",
            "04_flyer_luxury-home.png",
            "06_tablet-phone_close-more-deals.png",
            "02_spotlight_wholesaling-platform.png",
            "01_flyer_close-more-deals.png",
          ].map((file) => (
            <a key={file} href={`/downloads/affiliate/${file}`}
               target="_blank" rel="noopener noreferrer"
               className="block overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:border-amber-400 transition-colors"
               data-testid={`affiliate-preview-${file}`}>
              <img src={`/downloads/affiliate/${file}`}
                   alt="iSpeedToClose promo asset"
                   className="w-full aspect-[4/5] object-cover" loading="lazy"/>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function PolicyTile({ label, value, sub }) {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] mb-1">{label}</div>
      <div className="font-display font-extrabold text-3xl tracking-tight">{value}</div>
      <div className="text-xs text-[var(--ink-2)] mt-1">{sub}</div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm flex items-center justify-center">
        {n}
      </span>
      <span className="leading-relaxed pt-0.5">{children}</span>
    </li>
  );
}

function StatusBadge({ status }) {
  const colors = {
    pending: { bg: "#FEF3C7", fg: "#92400E", icon: Clock },
    active: { bg: "#ECFDF5", fg: "#065F46", icon: CheckCircle2 },
    paid: { bg: "#ECFDF5", fg: "#065F46", icon: CheckCircle2 },
    included_in_payout: { bg: "#DBEAFE", fg: "#1E40AF", icon: Clock },
    processing: { bg: "#DBEAFE", fg: "#1E40AF", icon: Loader2 },
    failed: { bg: "#FEE2E2", fg: "#991B1B", icon: AlertCircle },
    clawed_back: { bg: "#FEE2E2", fg: "#991B1B", icon: AlertCircle },
    expired: { bg: "#F1F5F9", fg: "#64748B", icon: Clock },
    suspended: { bg: "#FEE2E2", fg: "#991B1B", icon: AlertCircle },
    approved: { bg: "#ECFDF5", fg: "#065F46", icon: CheckCircle2 },
  };
  const c = colors[status] || colors.pending;
  const Icon = c.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`status-${status}`}
    >
      <Icon size={10} />
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function EmptyState({ text, cta }) {
  return (
    <div className="card p-8 text-center" data-testid="empty-state">
      <p className="text-[var(--ink-2)] mb-4">{text}</p>
      {cta && <div className="flex justify-center">{cta}</div>}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}


// ─── Phase 2A: Tier Badge ───────────────────────────────────────────────────
function TierBadge({ tier }) {
  if (!tier) return null;
  const TierIcon = tier.tier === "diamond" ? Crown
    : tier.tier === "gold" ? Trophy
    : tier.tier === "silver" ? Award
    : Sparkles;
  const progress = tier.progress || {};
  return (
    <div
      className="card p-5 relative overflow-hidden"
      data-testid="tier-badge"
      style={{
        background: `linear-gradient(135deg, ${tier.color}15 0%, white 60%)`,
        borderLeft: `4px solid ${tier.color}`,
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">Your Tier</div>
          <div className="flex items-center gap-2 mt-1">
            <TierIcon size={28} style={{ color: tier.color }} />
            <div>
              <div className="font-display font-black text-2xl tracking-tight">{tier.label}</div>
              <div className="text-xs font-bold" style={{ color: tier.color }}>
                {tier.commission_rate_pct}% commission rate
              </div>
            </div>
          </div>
        </div>
      </div>
      {progress.next_tier && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[var(--ink-3)]">
              Progress to <strong className="text-[var(--ink)] capitalize">{progress.next_tier_label}</strong>
            </span>
            <span className="font-bold">{progress.pct}%</span>
          </div>
          <div className="h-2 bg-[var(--bg-2)] rounded-full overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${Math.min(100, progress.pct)}%`, background: tier.color }}
              data-testid="tier-progress-bar"
            />
          </div>
          <div className="text-[10px] text-[var(--ink-3)] mt-2">
            {progress.referrals_needed > 0 && (
              <span>{progress.referrals_needed} more referral{progress.referrals_needed === 1 ? "" : "s"}</span>
            )}
            {progress.referrals_needed > 0 && progress.mrr_needed_cents > 0 && <span> OR </span>}
            {progress.mrr_needed_cents > 0 && (
              <span>${(progress.mrr_needed_cents / 100).toFixed(0)}/mo more MRR generated</span>
            )}
          </div>
        </div>
      )}
      {tier.perks?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {tier.perks.map((p, i) => (
            <li key={i} className="text-xs flex items-start gap-1 text-[var(--ink-2)]">
              <CheckCircle2 size={11} className="mt-0.5 shrink-0" style={{ color: tier.color }} />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ─── Phase 2A: Leaderboard ──────────────────────────────────────────────────
function Leaderboard({ rows, myRank }) {
  return (
    <div className="card p-5" data-testid="leaderboard">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">Leaderboard</div>
          <h3 className="font-display font-bold text-lg">Top affiliates this quarter</h3>
        </div>
        {myRank && (
          <div className="text-xs">
            <span className="text-[var(--ink-3)]">Your rank:</span>{" "}
            <span className="font-display font-black text-2xl text-emerald-600" data-testid="my-rank">#{myRank}</span>
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-8 text-[var(--ink-3)] text-sm">
          <Trophy size={28} className="mx-auto mb-2 opacity-40" />
          <p>No leaderboard activity yet. Be the first to make the board.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.slice(0, 10).map((r) => (
            <div
              key={r.rank}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--bg-2)]"
              data-testid={`leaderboard-row-${r.rank}`}
            >
              <span className="font-display font-black text-lg w-8 text-center text-[var(--ink-3)]">
                {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{r.display_name}</div>
                <div className="text-[10px] text-[var(--ink-3)]">
                  {r.active_referrals} active referral{r.active_referrals === 1 ? "" : "s"}
                </div>
              </div>
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ background: r.tier_color + "22", color: r.tier_color }}
              >
                {r.tier_label}
              </span>
              <div className="font-display font-bold text-sm text-right tabular-nums w-20">
                ${(r.lifetime_commission_cents / 100).toFixed(0)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Phase 2B: Deep-Link Generator ──────────────────────────────────────────
function LinksManager({ links, destinations, onChange }) {
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    destination_key: "pricing",
    label: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
  });

  const submit = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/affiliate/links`, form, { headers: authHeader() });
      toast.success("Link created!");
      setShowForm(false);
      setForm({ destination_key: "pricing", label: "", utm_source: "", utm_medium: "", utm_campaign: "" });
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create link");
    }
    setCreating(false);
  };

  const deleteLink = async (id) => {
    if (!window.confirm("Delete this link? Existing clicks remain attributed but the link will stop working.")) return;
    try {
      await axios.delete(`${API}/affiliate/links/${id}`, { headers: authHeader() });
      toast.success("Deleted");
      onChange();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const shortUrl = (slug) => `${window.location.origin}/api/affiliate/r/${slug}`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <section data-testid="links-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <Link2 size={18} /> Deep-Link Generator
          </h2>
          <p className="text-xs text-[var(--ink-3)]">
            Create trackable short links to specific landing pages, with UTM tags + QR codes.
          </p>
        </div>
        <button
          type="button"
          className="btn-money"
          onClick={() => setShowForm((v) => !v)}
          data-testid="links-new-btn"
        >
          <Plus size={14} /> New Link
        </button>
      </div>

      {showForm && (
        <div className="card p-4 mb-4 bg-emerald-50/30" data-testid="links-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">Destination</span>
              <select
                value={form.destination_key}
                onChange={(e) => setForm((f) => ({ ...f, destination_key: e.target.value }))}
                className="input mt-1"
                data-testid="links-destination"
              >
                {destinations.map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">Label (just for you)</span>
              <input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Twitter promo Q3"
                className="input mt-1"
                data-testid="links-label"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">utm_source</span>
              <input
                value={form.utm_source}
                onChange={(e) => setForm((f) => ({ ...f, utm_source: e.target.value }))}
                placeholder="twitter / youtube / newsletter"
                className="input mt-1"
                data-testid="links-utm-source"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">utm_medium</span>
              <input
                value={form.utm_medium}
                onChange={(e) => setForm((f) => ({ ...f, utm_medium: e.target.value }))}
                placeholder="social / email / video"
                className="input mt-1"
                data-testid="links-utm-medium"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-3)]">utm_campaign</span>
              <input
                value={form.utm_campaign}
                onChange={(e) => setForm((f) => ({ ...f, utm_campaign: e.target.value }))}
                placeholder="founder-launch / black-friday / podcast-ep-42"
                className="input mt-1"
                data-testid="links-utm-campaign"
              />
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button type="button" className="btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="button" className="btn-money" onClick={submit} disabled={creating} data-testid="links-save">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create link
            </button>
          </div>
        </div>
      )}

      {links.length === 0 ? (
        <div className="card p-8 text-center">
          <Link2 size={28} className="mx-auto text-[var(--ink-3)] opacity-40 mb-2" />
          <p className="text-[var(--ink-2)] text-sm">No deep-links yet. Create your first one — drop it on Twitter, YouTube, your newsletter, anywhere.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((l) => {
            const url = shortUrl(l.slug);
            const qr = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
            return (
              <div key={l.id} className="card p-4 flex flex-wrap items-center gap-3" data-testid={`link-row-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm">{l.label}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[var(--bg-2)] text-[var(--ink-3)]">
                      {l.destination_key}
                    </span>
                    {l.utm_source && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                        {l.utm_source}
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-mono text-[var(--ink-2)] truncate mt-1">{url}</div>
                  <div className="text-[10px] text-[var(--ink-3)] mt-1">
                    <span className="font-bold text-[var(--ink-2)]">{l.click_count || 0}</span> click{l.click_count === 1 ? "" : "s"}
                    {l.signup_count > 0 && <> · <span className="font-bold text-emerald-600">{l.signup_count}</span> signup{l.signup_count === 1 ? "" : "s"}</>}
                    {l.paid_conversion_count > 0 && <> · <span className="font-bold text-emerald-600">{l.paid_conversion_count}</span> paid</>}
                    · Created {fmtDate(l.created_at)}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-outline text-xs"
                  onClick={() => copy(url)}
                  data-testid={`link-copy-${l.id}`}
                >
                  <Copy size={12} /> Copy
                </button>
                <a
                  href={qr}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline text-xs"
                  data-testid={`link-qr-${l.id}`}
                >
                  <QrCode size={12} /> QR
                </a>
                <button
                  type="button"
                  className="btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50"
                  onClick={() => deleteLink(l.id)}
                  data-testid={`link-delete-${l.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
